import {
  calculateCostPerShift,
  calculateRvusPerShift,
  calculateCostPerHour as calculateSharedCostPerHour,
  calculatePricePerRVU as calculateSharedPricePerRVU,
} from '~/features/market/listings/components/costCalculatorMoney';
import type { ListingContract } from '~/types/hlx/msaasDetail';

export { calculateCostPerShift, calculateRvusPerShift };

const SHIFT_TYPE_ID_MAP = {
  Day: 0,
  Evening: 1,
  Night: 2,
};

const SHIFT_COUNT_KEYS = [
  'numDayShifts',
  'numEveShifts',
  'numNightShifts',
  'numOfWeDayShifts',
  'numOfWeEveShifts',
  'numOfWeNightShifts',
  'numHolDayShifts',
  'numHolEveShifts',
  'numHolNightShifts',
] satisfies (keyof ListingContract)[];

export const calculateDifferential = (
  detail: ListingContract,
  rowData: { diffKey: keyof ListingContract }
): number => Number(detail[rowData.diffKey] ?? 0);

export const calculateCostPerHour = (detail: ListingContract, differential: number): number =>
  calculateSharedCostPerHour(detail, differential, detail.shiftDiffType);

export const calculatePricePerRVU = (
  detail: ListingContract,
  differential: number,
  hourlyRate?: number
): number => calculateSharedPricePerRVU(detail, differential, detail.shiftDiffType, hourlyRate);

export const generateRowData = (
  detail: ListingContract,
  rowData: {
    diffKey: keyof ListingContract;
    countKey: keyof ListingContract;
    shiftType: string;
    tableIdx: number;
  }
) => {
  const shiftCount = Number(detail[rowData.countKey] ?? 0);
  const differential = calculateDifferential(detail, rowData);
  const hourlyRate = calculateCostPerHour(detail, differential);
  const costPerShift = calculateCostPerShift(detail, hourlyRate);

  return {
    id:
      detail.id +
      rowData.tableIdx * 10 +
      SHIFT_TYPE_ID_MAP[rowData.shiftType as keyof typeof SHIFT_TYPE_ID_MAP],
    shiftType: rowData.shiftType,
    count: shiftCount,
    baseRate: hourlyRate,
    differential,
    totalRate: Math.round(hourlyRate * 100) / 100,
    hoursPerShift: detail.hoursPerShift,
    costPerShift,
    subTotal: shiftCount * costPerShift,
    rvus: shiftCount * calculateRvusPerShift(detail),
    hourlyRate,
    pricePerRvu: calculatePricePerRVU(detail, differential, hourlyRate),
  };
};

export const checkIfShiftDetails = (detail: ListingContract): boolean =>
  SHIFT_COUNT_KEYS.some((key) => Number(detail[key] ?? 0) > 0);
